import { useQuery } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { api } from '../../lib/api';

interface DailyPoint {
  date: string;
  characters: number;
}
interface VoiceUsage {
  voiceId: string;
  characters: number;
}
interface ElevenLabsDetails {
  rangeDays: number;
  totalCharacters: number;
  daily30d: DailyPoint[];
  last7dCharacters: number;
  prev7dCharacters: number;
  trendPct: number | null;
  topVoices: VoiceUsage[];
  generatedAt: string;
}

function fetchDetails(): Promise<ElevenLabsDetails> {
  return api.get<ElevenLabsDetails>('/admin/health/elevenlabs/details');
}

export function ElevenLabsDetailsSection(): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['health', 'elevenlabs-details'],
    queryFn: fetchDetails,
    refetchInterval: 5 * 60_000,
  });

  if (isLoading) return <Box>Lädt …</Box>;
  if (error) {
    return (
      <Box dangerous>
        Fehler: {error instanceof Error ? error.message : 'unbekannt'}
      </Box>
    );
  }
  if (!data) return <Box>Keine Daten.</Box>;

  const trendColor =
    data.trendPct == null
      ? darkTheme.colors.textTertiary
      : data.trendPct > 25
      ? darkTheme.colors.warning
      : data.trendPct < -25
      ? darkTheme.colors.success
      : darkTheme.colors.textPrimary;

  return (
    <article
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <header className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-text-primary">ElevenLabs — Details</h3>
        <span className="text-[11px] text-text-tertiary">
          letzte {data.rangeDays} Tage
        </span>
      </header>

      <section className="mb-4 grid gap-3 sm:grid-cols-3">
        <Stat
          label="Σ Charakter 30d"
          value={data.totalCharacters.toLocaleString('de-DE')}
        />
        <Stat
          label="Letzte 7 Tage"
          value={data.last7dCharacters.toLocaleString('de-DE')}
          hint={`vs. Vorwoche: ${data.prev7dCharacters.toLocaleString('de-DE')}`}
        />
        <Stat
          label="Trend Wo/Vorwoche"
          value={
            data.trendPct == null
              ? '—'
              : `${data.trendPct > 0 ? '+' : ''}${data.trendPct.toFixed(0)}%`
          }
          color={trendColor}
        />
      </section>

      <section className="mb-4">
        <SectionTitle>Tagesverlauf — letzte 30 Tage</SectionTitle>
        <DailySparkline data={data.daily30d} />
      </section>

      {data.topVoices.length > 0 && (
        <section>
          <SectionTitle>Top Voices nach Verbrauch</SectionTitle>
          <div className="space-y-1">
            {data.topVoices.map((v) => {
              const pct =
                data.totalCharacters > 0
                  ? (v.characters / data.totalCharacters) * 100
                  : 0;
              return (
                <div key={v.voiceId} className="text-xs">
                  <div className="mb-0.5 flex items-baseline justify-between gap-2">
                    <span
                      className="truncate font-mono text-text-secondary"
                      title={v.voiceId}
                    >
                      {v.voiceId.slice(0, 12)}…
                    </span>
                    <span className="tabular text-text-primary">
                      {v.characters.toLocaleString('de-DE')} · {pct.toFixed(0)}%
                    </span>
                  </div>
                  <div
                    className="h-1 overflow-hidden rounded"
                    style={{ background: darkTheme.colors.bgElevated }}
                  >
                    <div
                      className="h-full"
                      style={{
                        width: `${Math.min(100, pct)}%`,
                        background: darkTheme.colors.accent,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <footer
        className="mt-4 border-t pt-2 text-[10px] text-text-tertiary"
        style={{ borderColor: darkTheme.colors.borderSubtle }}
      >
        Stand: {new Date(data.generatedAt).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })}
        {' '}· 1h Cache
      </footer>
    </article>
  );
}

function DailySparkline({ data }: { data: DailyPoint[] }): JSX.Element {
  const max = Math.max(...data.map((d) => d.characters), 1);
  return (
    <div className="flex items-end gap-[2px]" style={{ height: 64 }}>
      {data.map((d) => {
        const h = Math.max(2, (d.characters / max) * 60);
        return (
          <div
            key={d.date}
            title={`${d.date}: ${d.characters.toLocaleString('de-DE')} Zeichen`}
            className="flex-1 rounded-t"
            style={{
              height: h,
              background: darkTheme.colors.accent,
              opacity: d.characters > 0 ? 1 : 0.2,
            }}
          />
        );
      })}
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  color,
}: {
  label: string;
  value: string;
  hint?: string;
  color?: string;
}): JSX.Element {
  return (
    <div
      className="rounded-md border p-2"
      style={{ borderColor: darkTheme.colors.borderSubtle }}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
        {label}
      </div>
      <div
        className="mt-0.5 text-base font-bold tabular"
        style={{ color: color ?? darkTheme.colors.textPrimary }}
      >
        {value}
      </div>
      {hint && <div className="text-[10px] text-text-tertiary">{hint}</div>}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
      {children}
    </div>
  );
}

function Box({ children, dangerous }: { children: React.ReactNode; dangerous?: boolean }): JSX.Element {
  return (
    <div
      className="rounded-md border p-6 text-sm"
      style={{
        background: dangerous ? darkTheme.colors.dangerBg : darkTheme.colors.bgSurface,
        color: dangerous ? darkTheme.colors.danger : darkTheme.colors.textTertiary,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      {children}
    </div>
  );
}
